<!-- src/shared/components/base/BaseShowSecret.vue -->

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  /**
   * Base component for secret display implementations
   * Provides core secret loading/display logic and structural slots for customization
   *
   * @slot header - Page header content (logos, titles, etc)
   * @slot loading - Loading indicator while fetching secret
   * @slot error - Error display when something goes wrong
   * @slot alerts - Warning/notification alerts for secret owners (includes uiConfig)
   * @slot confirmation - Secret confirmation form and related content
   * @slot reveal - Secret display content when revealed
   * @slot onboarding - Additional content shown during confirmation (e.g., marketing)
   * @slot unknown - Content shown when secret is not found
   * @slot footer - Page footer content
   */

  import SecretSkeleton from '@/shared/components/closet/SecretSkeleton.vue';
  import { useSecret } from '@/shared/composables/useSecret';
  import { useSecretContext } from '@/shared/composables/useSecretContext';
  import { onMounted } from 'vue';
  import { onBeforeRouteUpdate } from 'vue-router';

  const { t } = useI18n();

  export interface Props {
    secretIdentifier: string;
    siteHost: string;
    domainStrategy?: string;
    displayDomain?: string;
    domainId?: string | null;
    branded?: boolean;
  }

  const props = defineProps<Props>();

  const { record, details, state, load, reveal } = useSecret(props.secretIdentifier);

  // Actor-based UI configuration derived from auth state and ownership
  const { uiConfig, actorRole } = useSecretContext({
    isOwner: () => details.value?.is_owner ?? false,
  });

  const handleUserConfirmed = (passphrase: string) => {
    reveal(passphrase);
  };

  onBeforeRouteUpdate((to, from, next) => {
    load();
    next();
  });

  onMounted(() => {
    load();
  });
</script>

<template>
  <main
    class="grid min-h-screen grid-rows-[auto_minmax(0,max-content)_auto] gap-4"
    role="main"
    :aria-label="t('web.secrets.secret_viewing_page')">
    <header
      v-if="$slots.header"
      class="w-full bg-white dark:bg-gray-900">
      <div class="mx-auto w-full max-w-4xl px-4">
        <slot
          name="header"
          :record="record"
          :details="details"></slot>
      </div>
    </header>

    <!-- Content wrapper  -->
    <div class="mx-auto w-full max-w-4xl px-4">
      <!-- Global Loading State -->
      <div
        v-if="state.isLoading"
        class="animate-pulse space-y-6 p-4">
        <SecretSkeleton />
      </div>

      <!-- Initial Loading - Prevent UnknownSecret flash -->
      <div
        v-else-if="!state.isLoading && !record && !state.error"
        class="animate-pulse space-y-6 p-4">
        <SecretSkeleton />
      </div>

      <!-- Unknown Secret State -->
      <template v-else-if="!record">
        <slot
          name="unknown"
          :branded="branded"
          :details="details">
        </slot>
      </template>

      <!-- Main Content - Valid Secret -->
      <div v-else-if="record && details">
        <!-- Alerts slot for owner warnings -->
        <slot
          name="alerts"
          :record="record"
          :details="details"
          :is-owner="details.is_owner"
          :show-secret="details.show_secret"
          :ui-config="uiConfig"
          :actor-role="actorRole"></slot>

        <template v-if="!details.show_secret">
          <!-- Confirmation form slot -->
          <slot
            name="confirmation"
            :secret-identifier="secretIdentifier"
            :record="record"
            :details="details"
            :error="state.error"
            :is-loading="state.isLoading"
            :on-confirm="handleUserConfirmed"></slot>

          <!-- Optional onboarding/marketing slot -->
          <slot
            name="onboarding"
            :record="record"
            :details="details"
            :ui-config="uiConfig"></slot>
        </template>

        <template v-else>
          <!-- Reveal content slot -->
          <slot
            name="reveal"
            :record="record"
            :details="details"></slot>
        </template>
      </div>
    </div>

    <!-- Footer wrapper -->
  </main>
</template>

<style scoped>
  /* Common base styles */
  :focus {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
</style>
